import type { MatrixClient, TokenRefreshFunction } from 'matrix-js-sdk/src/matrix';

import { emitSessionLifecycleEvent, logAuthLifecycle, sanitizeMatrixError } from '../sessionObservability';

export type PersistedMatrixTokens = {
  accessToken: string;
  refreshToken?: string;
  accessTokenExpiresAt?: string;
};

type MatrixRefreshResponse = {
  access_token: string;
  refresh_token?: string;
  expires_in_ms?: number;
};

const REFRESH_CIRCUIT_BREAKER_ERROR = 'Matrix token refresh permanently failed — circuit breaker open';

/**
 * Classifies the outcome of the most recent refresh attempt.
 * - `none`: no outstanding failure — refresh allowed.
 * - `transient`: network / 5xx failure — retried with backoff, never blocks future attempts.
 * - `permanent`: invalid refresh token (401 / `M_UNKNOWN_TOKEN`) — blocks all future refreshes
 *   for this session until a successful login or an explicit reset.
 */
export type RefreshFailureKind = 'none' | 'transient' | 'permanent';

/** Max retries for transient (network / 5xx) refresh failures before giving up for this attempt. */
const MAX_TRANSIENT_REFRESH_RETRIES = 3;
/** Exponential backoff between transient retries (ms). */
const TRANSIENT_REFRESH_BACKOFF_MS = [2_000, 4_000, 8_000] as const;

/** Shared across pre-refresh and SDK tokenRefreshFunction. Only `permanent` blocks future refreshes. */
let refreshFailureKind: RefreshFailureKind = 'none';

/**
 * Monotonically increasing counter, incremented on every successful refresh (in
 * `callMatrixRefreshToken`'s success path and in `resetMatrixRefreshCircuitBreaker`).
 * Used to detect stale 401 failures: if the generation advanced while a refresh
 * attempt was in-flight it means a concurrent Path-A refresh already succeeded and
 * consumed the single-use token — the 401 is stale and must not permanently open
 * the circuit breaker.
 */
let refreshGeneration = 0;

export const resetMatrixRefreshCircuitBreaker = (): void => {
  refreshGeneration++;
  refreshFailureKind = 'none';
};

export const getMatrixRefreshFailureKind = (): RefreshFailureKind => refreshFailureKind;

/** True only when a permanent (401 / unknown token) failure has opened the breaker. */
export const isMatrixRefreshCircuitOpen = (): boolean => refreshFailureKind === 'permanent';

export type MatrixRefreshTelemetry = {
  phase?: 'resume' | 'proactive' | 'sdk_callback' | 'session_logged_out_recovery' | 'recovery';
  mx_device_id?: string;
};

const asRefreshError = (err: unknown): { httpStatus?: number; errcode?: string; message?: string } =>
  (err ?? {}) as { httpStatus?: number; errcode?: string; message?: string };

const isPermanentRefreshError = (err: unknown): boolean => {
  const { httpStatus, errcode, message } = asRefreshError(err);
  if (httpStatus === 401) return true;
  if (errcode === 'M_UNKNOWN_TOKEN') return true;
  return typeof message === 'string' && message.includes('M_UNKNOWN_TOKEN');
};

const isRateLimitRefreshError = (err: unknown): boolean => {
  const { httpStatus, errcode } = asRefreshError(err);
  return httpStatus === 429 || errcode === 'M_LIMIT_EXCEEDED';
};

const sleep = (ms: number): Promise<void> => new Promise((resolve) => setTimeout(resolve, ms));

const refreshBaseAttrs = (telemetry?: MatrixRefreshTelemetry) => ({
  domain: 'refresh' as const,
  phase: telemetry?.phase,
  mx_device_id: telemetry?.mx_device_id,
  circuit_open: isMatrixRefreshCircuitOpen(),
});

export const callMatrixRefreshToken = async (
  client: MatrixClient,
  refreshToken: string,
  telemetry?: MatrixRefreshTelemetry
): Promise<MatrixRefreshResponse> => {
  if (refreshFailureKind === 'permanent') {
    logAuthLifecycle('Refresh blocked — circuit open', {
      ...refreshBaseAttrs(telemetry),
      action: 'refresh_complete',
      outcome: 'circuit_blocked',
      circuit_open: true,
    });
    throw new Error(REFRESH_CIRCUIT_BREAKER_ERROR);
  }

  logAuthLifecycle('Matrix token refresh attempt', {
    ...refreshBaseAttrs(telemetry),
    action: 'refresh_attempt',
    circuit_open: false,
  });

  // Snapshot the generation before starting. If it advances while we are in-flight
  // it means a concurrent Path-A refresh succeeded and already consumed the
  // single-use rotating token — any subsequent 401 on this attempt is stale.
  const startGeneration = refreshGeneration;

  for (let attempt = 0; ; attempt++) {
    try {
      const res = await client.refreshToken(refreshToken);
      // Increment generation BEFORE resetting the kind so any concurrent caller
      // that lands in the permanent-error branch sees the updated generation first.
      refreshGeneration++;
      refreshFailureKind = 'none';
      logAuthLifecycle('Matrix token refresh succeeded', {
        ...refreshBaseAttrs(telemetry),
        action: 'refresh_complete',
        outcome: 'success',
        failure_kind: 'none',
      });
      return res;
    } catch (err) {
      const sanitized = sanitizeMatrixError(err);

      // Invalid refresh token — permanently stop retrying and route to recovery,
      // UNLESS the generation advanced while we were in-flight (i.e. a concurrent
      // Path-A refresh already succeeded and consumed this single-use token).
      // In that case the 401 is stale: the circuit is already reset to 'none' by
      // the successful refresh, so we must not re-open it to 'permanent'.
      if (isPermanentRefreshError(err)) {
        if (startGeneration < refreshGeneration) {
          logAuthLifecycle(
            'Matrix token refresh stale 401 — superseded by concurrent success',
            {
              ...refreshBaseAttrs(telemetry),
              action: 'refresh_complete',
              outcome: 'stale_permanent_suppressed',
              circuit_open: false,
            },
            'warn',
            sanitized
          );
          throw err;
        }

        refreshFailureKind = 'permanent';
        logAuthLifecycle(
          'Matrix token refresh permanently failed',
          {
            ...refreshBaseAttrs(telemetry),
            action: 'refresh_complete',
            outcome: 'permanent_fail',
            circuit_open: true,
          },
          'warn',
          sanitized
        );
        throw err;
      }

      // Rate limiting is handled by the SDK's Retry-After backoff — do not open the breaker or fast-retry here.
      if (isRateLimitRefreshError(err)) {
        emitSessionLifecycleEvent({
          ...refreshBaseAttrs(telemetry),
          action: 'refresh_complete',
          outcome: 'transient_fail',
          failure_kind: sanitized.failure_kind,
          errcode: sanitized.errcode,
          http_status: sanitized.http_status,
        });
        throw err;
      }

      // Transient (network / 5xx): retry with exponential backoff, but never open the permanent breaker.
      if (attempt >= MAX_TRANSIENT_REFRESH_RETRIES) {
        refreshFailureKind = 'transient';
        logAuthLifecycle(
          'Matrix token refresh transient failure exhausted',
          {
            ...refreshBaseAttrs(telemetry),
            action: 'refresh_complete',
            outcome: 'transient_fail',
            retry_attempt: attempt + 1,
          },
          'warn',
          sanitized
        );
        throw err;
      }

      await sleep(TRANSIENT_REFRESH_BACKOFF_MS[attempt]);
    }
  }
};

const base64UrlEncode = (bytes: Uint8Array): string => {
  const binary = Array.from(bytes, (byte) => String.fromCharCode(byte)).join('');
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
};

export const generateSearchSecret = (): string => {
  const bytes = crypto.getRandomValues(new Uint8Array(32));
  return base64UrlEncode(bytes);
};

export const isAccessTokenExpired = (expiresAtIso: string | undefined): boolean => {
  if (!expiresAtIso) return false;
  return Date.now() >= new Date(expiresAtIso).getTime();
};

/** Refresh if access token expires within this window (ms). Used for proactive + tab-resume refresh. */
export const PROACTIVE_REFRESH_BUFFER_MS = 30_000;

export const isAccessTokenNearExpiry = (
  expiresAtIso: string | undefined,
  bufferMs = PROACTIVE_REFRESH_BUFFER_MS
): boolean => {
  if (!expiresAtIso) return false;
  return Date.now() >= new Date(expiresAtIso).getTime() - bufferMs;
};

export const toPersistedMatrixTokens = (res: MatrixRefreshResponse, refreshToken: string): PersistedMatrixTokens => ({
  accessToken: res.access_token,
  refreshToken: res.refresh_token ?? refreshToken,
  accessTokenExpiresAt: res.expires_in_ms ? new Date(Date.now() + res.expires_in_ms).toISOString() : undefined,
});

/** Apply refreshed tokens to cookies and the live Matrix client (in-memory SDK opts). */
export const applyRefreshedMatrixTokens = (
  client: MatrixClient,
  tokens: PersistedMatrixTokens,
  persistTokens: (tokens: PersistedMatrixTokens) => void
): void => {
  persistTokens(tokens);
  client.setAccessToken(tokens.accessToken);
  if (tokens.refreshToken) {
    client.http.opts.refreshToken = tokens.refreshToken;
  }
};

export const refreshAndApplyMatrixTokens = async (
  client: MatrixClient,
  refreshToken: string,
  persistTokens: (tokens: PersistedMatrixTokens) => void,
  telemetry?: MatrixRefreshTelemetry
): Promise<PersistedMatrixTokens> => {
  const res = await callMatrixRefreshToken(client, refreshToken, telemetry);
  resetMatrixRefreshCircuitBreaker();
  const tokens = toPersistedMatrixTokens(res, refreshToken);
  applyRefreshedMatrixTokens(client, tokens, persistTokens);
  return tokens;
};

export const createMatrixTokenRefreshFunction =
  (
    getClient: () => MatrixClient,
    persistTokens: (tokens: PersistedMatrixTokens) => void,
    telemetry?: MatrixRefreshTelemetry
  ): TokenRefreshFunction =>
  async (refreshToken: string) => {
    const res = await callMatrixRefreshToken(getClient(), refreshToken, {
      ...telemetry,
      phase: telemetry?.phase ?? 'sdk_callback',
    });
    const tokens = toPersistedMatrixTokens(res, refreshToken);

    persistTokens(tokens);

    return {
      accessToken: tokens.accessToken,
      refreshToken: tokens.refreshToken,
      expiry: tokens.accessTokenExpiresAt ? new Date(tokens.accessTokenExpiresAt) : undefined,
    };
  };
